import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { isNil, omitBy, isEmpty, filter, omit, has } from 'lodash';
import { useAddContentHeader, useQuery, useSession, useItemsPerPage } from 'Hooks/index';
import { Space } from 'antd';
import Icon, { PlusOutlined, DatabaseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
// import { encode } from "Helpers/enc";
import { AvatarTitle } from "Components/shared";
import { Button, ActionMenu, Pagination, PaginationChangeValue } from "@life-line-lab/components-react";
import { accountAtom } from 'Recoil/account.atom';
import { DEFAULT_MAX_PER_PAGE } from 'Components/shared/constants';
import { getUserList } from 'Services/account.services';

import AccountTable from 'Components/account/account-table/account-table';
import AccountTableNew from 'Components/account/account-table/account-table-new';

interface SortingState {
    sortBy: string,
    mode: string
}

const AccountListingPageNew = () => {

    const query = useQuery();
    const history = useHistory();
    const { t } = useTranslation();
    const { roleTypes, org } = useSession();
    const orgID: string = org._id;
    const page = query.get('page') || 1;
    const [itemPerPage, setItemPerPage] = useItemsPerPage(DEFAULT_MAX_PER_PAGE);
    const initialFilters = { orgID };
    const initialQueryParams = { page, offset: (page - 1) * itemPerPage }
    const initialSorting = { sortBy: 'updatedAt', mode: 'DESC' };

    const [queryParams, setQueryParams] = useState(initialQueryParams);
    const [filters, setFilters] = useState<{ [key: string]: string | boolean }>(initialFilters);
    const [sorting, setSorting] = useState<SortingState>(initialSorting);
    const [accountState, setAccountState] = useRecoilState<any>(accountAtom);
    const [isActiveOnly, setIsActiveOnly] = useState<boolean>(false);

    const isLoading = accountState.status.fetch === 'idle' || accountState.status.fetch === 'loading';

    useEffect(() => {
        setAccountState({ ...accountState, status: { fetch: 'loading' } });
        fetch();
    }, [queryParams.offset, filters, sorting]);

    // useEffect(() => {
    //     setAccountState({ ...accountState, list: accountState.list });
    // }, [accountState.list])

    useEffect(() => {
        if (accountState.list.length >= itemPerPage) {
            setAccountState({ ...accountState, status: { fetch: 'loading' } });
            fetch();
        }
    }, [itemPerPage]);

    const dummyData = [
        {
            fullName: 'hello',
            gender: 'male',
            contactNumber: '+60123456789',
            status: 'ACTIVE',
        },
    ];

    const fetch = async () => {
        let responseData: any;
        responseData = await getUserList({
            filters: {
                ...omit(filters, ['status']),
                onlyActive: isActiveOnly
            },
            pagination: {
                offset: queryParams.offset,
                limit: itemPerPage,
            },
            sorting: sorting,
        });

        if (!isNil(responseData)) {
            setAccountState({ ...accountState, status: { fetch: 'success' } });

            const sList = (filters.status == '' || filters.status == 'ALL' || has(filters, 'status') == false) ? responseData : responseData.filter((user: any) => {
                return user.status == filters.status;
            });

            // setAccountState({ ...accountState, list: sList });
            setAccountState({ ...accountState, list: sList });
        }
        else {
            setAccountState({ ...accountState, status: { fetch: 'error' } });
        }
    }

    const handleSearch = async (input: string) => {
        const filtered = filter(accountState.list, function (listItem) {
            let match = (listItem.fullName.toLowerCase().match(input.toLowerCase())
                || listItem.identificationNumber.toLowerCase().match(input.toLowerCase())
                || listItem._id.match(input.toLowerCase())) ? true : false;

            if (match === true) {
                return listItem;
            }
        });
        setAccountState({ ...accountState, list: filtered });
    }

    const handleFilter = (extraFilters: { [key: string]: string | boolean }) => {
        // const tempList = extraFilters.status == 'ALL' ? accountState.list : accountState.list.filter((user: any) => {
        //     return user.status == extraFilters.status;
        // });

        // setAccountState({ ...accountState, list: tempList });

        setFilters(omitBy({
            ...filters,
            ...extraFilters
        }, (value) => isNil(value)))
    }

    const handleToggleRoleStatus = async (profileID: string, role: any, active: boolean) => {
        setAccountState({ ...accountState, update: 'loading' });

        const responseData: any = await updateUserRoleStatus({
            orgID,
            profileID,
            accountID: role.accountID,
            type: role.type,
            subType: role.subType,
            status: active ? 'ACTIVE' : 'DEACTIVATED'
        });

        if (!isNil(responseData.error)) {
            setAccountState({ ...accountState, update: 'error' });
            console.log(responseData?.error)
        }
        else {
            setAccountState({ ...accountState, update: 'success' });
            // fetch();
        }
    }

    const handleItemsPerPageChange = (value: string) => {
        setItemPerPage(value);
    }

    const handlePaginationChange = (value: any) => {
        let page = queryParams.page;
        let limit = itemPerPage;

        if (value === 'NEXT') {
            if (accountState.list.length == itemPerPage) ++page;
            if (page > itemPerPage) page = itemPerPage;
        }

        if (value === 'PREVIOUS') {
            --page;
            if (page < 1) page = 1;
        }

        const offset = (page - 1) * limit;
        setQueryParams({ ...queryParams, page, offset });

        if (offset > 0)
            history.push({
                pathname: '/accounts',
                search: `?page=${page}`
            });
        else
            history.push('/accounts');
    }

    const handleOpenStackScreen = () => {
        history.push('/accounts/create');
    }

    // CONTENT HEADER
    useAddContentHeader(
        <Space direction="horizontal">
            <AvatarTitle isLoading={false} text={t('title.accounts')} />

            <div className="right">
                <Button
                    type="primary"
                    size="large"
                    icon={<PlusOutlined />}
                    label={t('action.addNew')}
                    onClick={handleOpenStackScreen}
                />
            </div>
        </Space>
        , isLoading)

    return (
        <React.Fragment>
            <Helmet>
                <title>{t('title.accounts')}</title>
            </Helmet>

            <div className="account-page">
                <div className="page-body pb-50">
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <AccountTableNew
                            list={accountState.list || []}
                            isShowFilter={true}
                            isLoading={false}
                            // permissionRights={permission.rights}
                            orgID={filters.orgID}
                            onRoleStatusChange={handleToggleRoleStatus}
                            onSearch={handleSearch}
                            onFilter={handleFilter} />


                        <div className="my-10">
                            <Pagination
                                previousIcon={<LeftOutlined />}
                                nextIcon={<RightOutlined />}
                                itemsPerPageValue={itemPerPage}
                                itemsPerPageConfig={{
                                    initialValue: DEFAULT_MAX_PER_PAGE,
                                    prefixLabel: t('label.show'),
                                    suffixLabel: t('txt.records')
                                }}
                                onItemsPerPageChange={handleItemsPerPageChange}
                                onChange={handlePaginationChange}
                            />
                        </div>
                    </Space>
                </div>
            </div>
        </React.Fragment>
    )
}

export default AccountListingPageNew
